/**
 * Contract milestone dates (Delivery / Setup / …) stored as compound objects:
 * { start, end?, notificationEmails, options? }
 * Sheet templates usually expose only {{delivery | start}}; DocHub V2 edits the group in a popup.
 */

import {
  APP_ROLES,
  CONTRACTOR_CATEGORY_OPTIONS,
  ROLE_ADMIN,
  ROLE_SALES,
  ROLE_SALES_AND_INVENTORY_MANAGER,
  ROLE_SALES_MANAGER,
  ROLE_SUPER_ADMIN,
  contractMilestoneDates
} from '../../helpers/constants';
import { getFormattedDate, getFormattedDateTime } from '../../form-generator/helpers/utility';

const MILESTONE_BY_KEY = Object.fromEntries(contractMilestoneDates.map(m => [m.key, m]));

const NOTIFICATION_OPTIONS_APP_CHOICE = {
  setupNotificationOptions: 'Setup Notification Options',
  serviceNotificationOptions: 'Service Notification Options',
  deliveryNotificationOptions: 'Delivery Notification Options',
  makereadyNotificationOptions: 'Makeready Notification Options'
};

const TO_BE_GROUPED_BY_SALES_LOCATION = [
  ROLE_SUPER_ADMIN,
  ROLE_ADMIN,
  ROLE_SALES,
  ROLE_SALES_MANAGER,
  ROLE_SALES_AND_INVENTORY_MANAGER
];

export const getMilestoneConfig = key => MILESTONE_BY_KEY[key] || null;

/** Path like ['delivery','start'] or ['delivery'] → milestone config */
export const getMilestoneConfigFromPath = path => {
  if (!path?.length) return null;
  return getMilestoneConfig(path[0]);
};

export const isMilestonePath = path => Boolean(getMilestoneConfigFromPath(path));

/** Walkthrough is date-only; paired milestones use datetime-local (matches contract form). */
export const getMilestoneDateInputType = config =>
  config?.end ? 'datetime-local' : 'date';

export const toDatetimeLocalValue = value => {
  if (value === undefined || value === null || value === '') return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return getFormattedDateTime(date);
};

export const toDateInputValue = value => {
  if (value === undefined || value === null || value === '') return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return getFormattedDate(date);
};

export const toMilestoneCellDisplayValue = (milestone, config) => {
  const start = milestone?.start;
  if (!start) return '';
  return getMilestoneDateInputType(config) === 'date'
    ? toDateInputValue(start)
    : toDatetimeLocalValue(start);
};

/** Match FormGenerator datetime-local → ISO (local wall time). */
export const fromDatetimeLocalValue = dateString => {
  if (!dateString) return '';
  try {
    const [datePart, timePart = '00:00'] = String(dateString).split('T');
    const date = new Date(datePart.split('-').join('/'));
    const [hour, min] = timePart.split(':');
    date.setHours(Number(hour) || 0, Number(min) || 0, 0, 0);
    return date.toISOString();
  } catch (e) {
    return '';
  }
};

export const fromDateInputValue = dateString => {
  if (!dateString) return '';
  try {
    return new Date(String(dateString).split('-').join('/')).toISOString();
  } catch (e) {
    return '';
  }
};

export const milestoneInputToIso = (value, config) =>
  getMilestoneDateInputType(config) === 'date'
    ? fromDateInputValue(value)
    : fromDatetimeLocalValue(value);

/**
 * Flatten stored milestone for the edit form (buyer email → buyer-mhc sentinel).
 */
export const normalizeMilestoneForEdit = (milestone, buyerEmail) => {
  const src = milestone && typeof milestone === 'object' ? milestone : {};
  const emails = Array.isArray(src.notificationEmails) ? src.notificationEmails : [];
  return {
    start: src.start || '',
    end: src.end || '',
    notificationEmails: emails.map(e => (buyerEmail && e === buyerEmail ? 'buyer-mhc' : e)),
    options: Array.isArray(src.options) ? [...src.options] : []
  };
};

/**
 * Same rules as validateAndCleanupForm for one milestone group.
 * @returns {{ ok: true, value: object|null } | { ok: false, message: string }}
 */
export const validateAndCleanupMilestone = (draft, config, buyerEmail) => {
  if (!config) return { ok: false, message: 'Unknown milestone' };

  const start = draft?.start || '';
  const end = draft?.end || '';
  const emails = Array.isArray(draft?.notificationEmails) ? draft.notificationEmails : [];
  const options = Array.isArray(draft?.options) ? draft.options : [];

  if (!start && !end) {
    return { ok: true, value: null };
  }

  if (start && emails.length === 0) {
    return { ok: false, message: `Please select who to notify for ${config.name}` };
  }

  if (config.end && ((end && !start) || (start && !end))) {
    return { ok: false, message: 'Both start and end time are required' };
  }

  if (config.end && start && end && new Date(end).getTime() < new Date(start).getTime()) {
    return { ok: false, message: 'End time cannot be less than start time' };
  }

  const value = {
    start,
    notificationEmails: emails
      .map(e => (e === 'buyer-mhc' ? buyerEmail : e))
      .filter(e => Boolean(e))
  };

  if (config.end) {
    value.end = end;
    value.options = options;
  }

  return { ok: true, value };
};

/** Validate every milestone currently present on documentState (save / print). */
export const validateDocumentMilestones = documentState => {
  for (const config of contractMilestoneDates) {
    const current = documentState?.[config.key];
    if (current == null || current === '') continue;
    if (typeof current !== 'object') continue;

    const draft = {
      start: current.start || '',
      end: current.end || '',
      notificationEmails: Array.isArray(current.notificationEmails)
        ? current.notificationEmails
        : [],
      options: Array.isArray(current.options) ? current.options : []
    };

    // Already stored emails are real addresses (buyer-mhc already resolved).
    const result = validateAndCleanupMilestone(draft, config, documentState?.buyerEmail);
    if (!result.ok) return result;
  }
  return { ok: true };
};

export const buildMilestoneEmailOptions = appChoices => {
  const members = appChoices?.find(c => c.key === 'members')?.values || [];
  const contractors = appChoices?.find(c => c.key === 'generalContractor')?.values || [];

  const emailOptions = [
    { name: 'Buyer in Contract (If available)', email: 'buyer-mhc', role: 'Other' },
    ...members,
    ...contractors.map(c => ({
      ...c,
      role: c.category || 'Contractor',
      type: 'contractors'
    }))
  ];

  const roleOrder = [...APP_ROLES, ...CONTRACTOR_CATEGORY_OPTIONS];
  emailOptions.sort((e1, e2) => {
    const e1RoleIndex = roleOrder.indexOf(e1.role);
    const e2RoleIndex = roleOrder.indexOf(e2.role);
    const roleCompare = e1RoleIndex - e2RoleIndex;
    return roleCompare === 0 ? String(e1.name || '').localeCompare(String(e2.name || '')) : roleCompare;
  });

  return {
    options: emailOptions.map(e => e.name),
    optionValues: emailOptions.map(e => e.email),
    groupValues: emailOptions.map(e =>
      TO_BE_GROUPED_BY_SALES_LOCATION.includes(e.role)
        ? e.salesLocation?.location || e.role
        : e.role
    )
  };
};

/** react-select grouped options for notify emails (matches V1 MultiSelectDropDown groups). */
export const buildMilestoneEmailSelectOptions = appChoices => {
  const { options, optionValues, groupValues } = buildMilestoneEmailOptions(appChoices);
  const grouped = {};

  options.forEach((label, index) => {
    const group = groupValues[index] || 'Other';
    if (!grouped[group]) grouped[group] = [];
    grouped[group].push({ label, value: optionValues[index] });
  });

  return Object.keys(grouped)
    .sort((a, b) => (a === 'Other' ? 1 : b === 'Other' ? -1 : a.localeCompare(b)))
    .map(label => ({ label, options: grouped[label] }));
};

export const buildMilestoneNotificationOptions = (appChoices, config) => {
  if (!config?.options) return { options: [], optionValues: [] };
  const appKey = NOTIFICATION_OPTIONS_APP_CHOICE[config.options];
  const values = appChoices?.find(c => c.key === appKey)?.values || [];
  const strings = values.map(v => (typeof v === 'object' ? v.name || v.key || String(v) : String(v)));
  return { options: strings, optionValues: strings };
};

export const buildMilestoneNotificationSelectOptions = (appChoices, config) => {
  const { options, optionValues } = buildMilestoneNotificationOptions(appChoices, config);
  return options.map((label, index) => ({
    label,
    value: optionValues[index]
  }));
};
